import PromiseQueue from 'promise-queue';
import { fetchAccessTokenIfNeeded } from '~/AccessToken';
import { retryFetchAfterUserProductGroupLock } from '~/Api/User/ProductGroup';
import {
	createExport,
	CreateExportResult,
	ErrorResponse,
	findGroup,
	findList,
	findSavingGroups,
	Group,
	GroupUpdate,
	ListResult,
	ProductUpdate,
	SavingGroupsResult,
	updateGroup as apiUpdateGroup,
	updateList,
	ValidationResult,
} from '~/Api/User/ProductGroup/CzReimbursement';
import { invokeApi, resolve, tryResolve } from '~/ApiClient';
import { handleError } from '~/ErrorHandler';
import { flashErrorMessage } from '~/FlashMessage/actions';
import { fetchReferenceBasketVersions as fetchCzPriceReferenceBasketVersions, Module } from '~/PriceReferencing';
import { fetchGroups, Filter } from '~/ProductGroup';
import { ThunkAction } from '~/Store';

import { Action, ActionType } from './Action';
import { selectGroup } from './reducer';

const updateQueue = new PromiseQueue(1, Infinity);

export function fetchCzReimbursement(groupId: string): ThunkAction<Promise<ListResult>, Action> {
	return async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = dispatch(invokeApi(findList, userId, groupId));
		dispatch({ type: ActionType.request, groupId, request });

		return resolve(request, (response) =>
			dispatch({
				type: ActionType.receive,
				groupId,
				request,
				czReimbursement: response?.czReimbursement || null,
			})
		);
	};
}

export function updateCzReimbursement(
	groupId: string,
	packageSize: number | null,
	dosageStrength: number | null,
	usualTherapeuticDailyDose: number | null,
	usualTherapeuticDailyDosePerPackage: number | null,
	products: ProductUpdate[]
): ThunkAction<Promise<void>, Action> {
	return (dispatch) => {
		const request = updateQueue.add(() =>
			dispatch(
				immediatelyUpdateCzReimbursement(
					groupId,
					packageSize,
					dosageStrength,
					usualTherapeuticDailyDose,
					usualTherapeuticDailyDosePerPackage,
					products
				)
			)
		);
		dispatch({ type: ActionType.requestUpdate, groupId, request });
		return request;
	};
}

function immediatelyUpdateCzReimbursement(
	groupId: string,
	packageSize: number | null,
	dosageStrength: number | null,
	usualTherapeuticDailyDose: number | null,
	usualTherapeuticDailyDosePerPackage: number | null,
	products: ProductUpdate[]
): ThunkAction<Promise<void>, Action> {
	return async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = retryFetchAfterUserProductGroupLock(() =>
			dispatch(
				invokeApi(
					updateList,
					userId,
					groupId,
					packageSize,
					dosageStrength,
					usualTherapeuticDailyDose,
					usualTherapeuticDailyDosePerPackage,
					products
				)
			)
		);
		dispatch({ type: ActionType.requestUpdate, groupId, request });

		await resolve(request, (response) =>
			dispatch({
				type: ActionType.receiveUpdate,
				groupId,
				request,
				czReimbursement: response?.czReimbursement || null,
			})
		);
	};
}

export function handleCzReimbursementUpdateResponse(response: any, groupId: string): ThunkAction<void, Action> {
	return (dispatch) => {
		if (!(response instanceof ErrorResponse)) {
			dispatch(handleError(response));
			return;
		}

		if (response.handleProductGroupNotFound()) {
			dispatch(fetchGroup(groupId));
		}
		if (response.handleCannotChangeSettingsForNonMemberProducts()) {
			dispatch(fetchCzReimbursement(groupId)).catch((response) => handleError(response));
		}
		if (response.handleIllegalPriceTypeSelected()) {
			dispatch(flashErrorMessage('Product reference prices has changed, please check selected product prices'));
			dispatch(fetchCzReimbursement(groupId)).catch((response) => handleError(response));
		}
		dispatch(handleError(response));
	};
}

export function fetchGroup(groupId: string): ThunkAction<Promise<void>, Action> {
	return async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = dispatch(invokeApi(findGroup, userId, groupId));
		dispatch({ type: ActionType.requestGroup, groupId, request });

		await tryResolve(request, (response, error) => {
			dispatch({
				type: ActionType.receiveGroup,
				groupId,
				request,
				group: response?.czReimbursement || null,
			});
			if (error !== null) {
				if (error instanceof ErrorResponse) {
					error.handleProductGroupNotFound();
				}
				dispatch(handleError(error));
			}
		});
	};
}

export function updateGroup(
	groupId: string,
	createUpdate: (group: Group) => GroupUpdate
): ThunkAction<Promise<void>, Action> {
	return async (dispatch, getState) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = updateQueue.add(() => {
			const group = selectGroup(getState().czReimbursement, groupId);
			if (group === null || group.group === null) {
				return null;
			}
			const update = createUpdate(group.group);
			return dispatch(invokeApi(apiUpdateGroup, userId, groupId, update));
		});

		if (request === null) {
			return;
		}

		dispatch({ type: ActionType.requestGroupUpdate, request, groupId });

		await resolve(request, (response, error) => {
			dispatch({
				type: ActionType.receiveGroupUpdate,
				request,
				error,
				groupId,
				group: response?.czReimbursement || null,
			});
			if (error === null) {
				return;
			}
			if (!(error instanceof ErrorResponse)) {
				dispatch(handleError(error));
			} else if (error.handleSettingsUnknownSavingsGroups()) {
				dispatch(
					flashErrorMessage(`
					Possible savings groups ${error
						.findUnknownSavingsGroups()
						.join(', ')} are no longer allowed, please remove them and try again`)
				);
			} else if (error.handleCannotChangeSettingsForNonMemberProductLists()) {
				dispatch(fetchGroups(new Filter(groupId, null), 1, 1000));
			} else {
				dispatch(handleError(error));
			}
		});
	};
}

export function createCzReimbursementExportXls(groupId: string): ThunkAction<Promise<CreateExportResult>, Action> {
	return async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = dispatch(invokeApi(createExport, userId, groupId));
		dispatch({ type: ActionType.requestXlsCreate, groupId, request });

		return resolve(request, (response, error) =>
			dispatch({
				type: ActionType.receiveXlsCreate,
				request,
				validationResult: error
					? error instanceof ErrorResponse
						? error.toValidationResult()
						: null
					: new ValidationResult(groupId, false, [], []),
			})
		);
	};
}

export function pruneCzReimbursement(groupId: string): Action {
	return { type: ActionType.prune, groupId };
}

export function scheduleCzReimbursementUpdate(
	groupId: string,
	packageSize: number | null,
	dosageStrength: number | null,
	usualTherapeuticDailyDose: number | null,
	usualTherapeuticDailyDosePerPackage: number | null,
	products: ProductUpdate[]
): ThunkAction<Promise<void>, Action> {
	return (dispatch) => {
		return new Promise((resolve) => {
			const timeoutId = setTimeout(() => {
				resolve(
					dispatch(
						updateCzReimbursement(
							groupId,
							packageSize,
							dosageStrength,
							usualTherapeuticDailyDose,
							usualTherapeuticDailyDosePerPackage,
							products
						)
					)
				);
			}, 2000);

			dispatch({
				type: ActionType.scheduleUpdate,
				timeoutId,
				groupId,
				packageSize,
				dosageStrength,
				usualTherapeuticDailyDose,
				usualTherapeuticDailyDosePerPackage,
				products,
			});
		});
	};
}

export function commitCzReimbursementUpdate(groupId: string): ThunkAction<Promise<void>, Action> {
	return async (dispatch, getState) => {
		const { update } = getState().czReimbursement;
		if (!update || update?.groupId !== groupId) {
			return;
		}
		if (update.state.name === 'fetching') {
			await update.state.request;
		}
		if (update.state.name === 'scheduled') {
			const { state } = update;
			clearTimeout(state.timeoutId);
			await dispatch(
				updateCzReimbursement(
					groupId,
					state.packageSize,
					state.dosageStrength,
					state.usualTherapeuticDailyDose,
					state.usualTherapeuticDailyDosePerPackage,
					state.products
				)
			);
		}
	};
}

export function handleCommitError(error: any): ThunkAction<void, Action> {
	return (dispatch) => {
		if (!(error instanceof ErrorResponse)) {
			dispatch(handleError(error));
			return;
		}

		error.handleProductGroupNotFound();
		error.handleCannotChangeSettingsForNonMemberProducts();
		error.handleIllegalPriceTypeSelected();
		dispatch(handleError(error));
	};
}

export function fetchReferenceBasketVersions(): ThunkAction<Promise<void>, Action> {
	return async (dispatch) => {
		await dispatch(fetchCzPriceReferenceBasketVersions(Module.czReimbursement));
	};
}

export function fetchSavingsGroups(groupId: string): ThunkAction<Promise<SavingGroupsResult>, Action> {
	return async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		return dispatch(invokeApi(findSavingGroups, userId, groupId));
	};
}
